import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import ProtectedRoute from "src/components/ProtectedRoute/ProtectedRoute";
import { getCurrentEnvironment, PandoraEnvironment, UserConfiguration, getUserConfiguration, updateUserConfiguration } from "src/services/ConfigurationService";
import ErrorPage from "../pages/Error/Error";
import UserService from "src/services/UserService";

type EnvironmentContextType = {
  //Page triggers
  loading: boolean;
  setLoading: (b: boolean) => void,

  //Configuration
  configuration?: UserConfiguration;
  currentEnvironment?: PandoraEnvironment;

  //Actions on configurations
  switchEnvironment: (environmentId: string) => Promise<boolean>;
};

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(
  undefined,
);

export const EnvironmentProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [configurationError, setConfigurationError] = useState(false);
  const [documentReferenceId, setDocumentReferenceId] = useState<string | undefined>(undefined);

  const [configuration, setConfiguration] = useState<UserConfiguration | undefined>(undefined);
  const [switching, setSwitching] = useState<boolean>(false);

  /**
  * Scroll to top on navigation
  */
  useEffect(() => {
    async function initConfig() {
      try {
        if (!UserService.isAuthenticated()) {
          setConfiguration(undefined);
          return;
        }
        const result = await getUserConfiguration();
        setDocumentReferenceId(result.documentReferenceId);
        setConfiguration(result.config);
      } catch (e) {
        console.log(e);
        setConfigurationError(true);
      } finally {
        setLoading(false);
      }
    }
    
    initConfig();
  }, []);


  const switchEnvironment = async (environmentId: string): Promise<boolean> => {
    if (!configuration) return false;
    if (switching) return false;

    setSwitching(true);

    const exists = configuration.environments?.find(
      (env) => env.id === environmentId,
    );
    if (!exists) return false;


    const updatedConfig: UserConfiguration = {
      ...configuration,
      currentEnvironmentId: environmentId,
    };

    try {
      await updateUserConfiguration(documentReferenceId ?? "", updatedConfig);
      setConfiguration(updatedConfig);
      window.location.reload();
      return true;
    } catch {
      return false;
    } finally {
      setSwitching(false);
    }
  };

  const currentEnvironment = useMemo(() => {
    return getCurrentEnvironment(configuration);
  }, [configuration]);

  return (
    <EnvironmentContext.Provider
      value={{
        loading,
        setLoading,
        configuration,
        currentEnvironment,
        switchEnvironment,
      }}
    >
      {loading && !configurationError && <div>Loading configuration...</div>}
      
      {configurationError &&
          <ProtectedRoute needsLogin={false}>
            <ErrorPage/>
          </ProtectedRoute>
      }
      
      {!loading && !configurationError && children}
    </EnvironmentContext.Provider>
  );
};

export function useEnvironment() {
  const context = useContext(EnvironmentContext);

  if (!context) {
    throw new Error("useEnvironment must be used inside EnvironmentProvider");
  }

  return context;
}
